import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge, PriorityBadge, SLABadge, PRIORITY_OPTIONS, CATEGORY_OPTIONS, MODULE_OPTIONS } from "@/utils/constants";
import { formatDate, formatDateTime } from "@/utils/formatters";
import { Clock, User, Eye, EyeOff } from "lucide-react";

const STATUS_LABELS = { open: "Open", assigned: "Assigned", in_progress: "In Progress", on_hold: "On Hold", resolved: "Resolved", closed: "Closed", reopened: "Reopened" };

export function DetailsPanel({ ticket, agents, currentStatus, canEditFields, editPriority, setEditPriority, editCategory, setEditCategory, editAssignee, setEditAssignee }) {
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm">
      <CardHeader className="pb-2"><CardTitle className="font-heading text-sm font-bold tracking-tight">Details</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <DetailRow label="Status"><StatusBadge status={currentStatus} /></DetailRow>
        <DetailRow label="Priority">
          {canEditFields ? (
            <Select value={editPriority || ticket.priority} onValueChange={setEditPriority}>
              <SelectTrigger className="h-7 w-[120px] rounded-sm text-xs" data-testid="edit-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITY_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          ) : (<PriorityBadge priority={ticket.priority} />)}
        </DetailRow>
        <DetailRow label="Category">
          {canEditFields ? (
            <Select value={editCategory || ticket.category} onValueChange={setEditCategory}>
              <SelectTrigger className="h-7 w-[140px] rounded-sm text-xs" data-testid="edit-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          ) : (<span className="text-sm capitalize">{ticket.category?.replace("_", " ")}</span>)}
        </DetailRow>
        <DetailRow label="Module">
          <span className="text-sm">{MODULE_OPTIONS.find((m) => m.value === ticket.module)?.label || ticket.module || "—"}</span>
        </DetailRow>
        <DetailRow label="Assignee">
          {canEditFields ? (
            <Select value={editAssignee || ticket.assigned_to || ""} onValueChange={setEditAssignee}>
              <SelectTrigger className="h-7 w-[140px] rounded-sm text-xs" data-testid="edit-assignee"><SelectValue placeholder="Select agent..." /></SelectTrigger>
              <SelectContent>{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          ) : (<span className="text-sm flex items-center gap-1"><User size={13} /> {ticket.assigned_to_name || "Unassigned"}</span>)}
        </DetailRow>
        {ticket.due_date && <DetailRow label="Due Date"><span className="text-sm flex items-center gap-1"><Clock size={13} /> {formatDate(ticket.due_date)}</span></DetailRow>}
        <DetailRow label="Created"><span className="text-xs text-[#52525B]">{formatDateTime(ticket.created_at)}</span></DetailRow>
        {ticket.resolved_at && <DetailRow label="Resolved"><span className="text-xs text-[#52525B]">{formatDateTime(ticket.resolved_at)}</span></DetailRow>}
      </CardContent>
    </Card>
  );
}

export function SLAPanel({ slaStatus, ticket }) {
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm" data-testid="sla-card">
      <CardHeader className="pb-2"><CardTitle className="font-heading text-sm font-bold tracking-tight">SLA</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center justify-between"><span className="text-xs text-[#52525B]">Response</span><SLABadge type="response" status={slaStatus.response} /></div>
        {ticket.sla_response_due && <p className="text-[10px] text-[#52525B]">Due: {formatDateTime(ticket.sla_response_due)}</p>}
        <Separator />
        <div className="flex items-center justify-between"><span className="text-xs text-[#52525B]">Resolution</span><SLABadge type="resolution" status={slaStatus.resolution} /></div>
        {ticket.sla_resolution_due && <p className="text-[10px] text-[#52525B]">Due: {formatDateTime(ticket.sla_resolution_due)}</p>}
      </CardContent>
    </Card>
  );
}

export function StatusTransitionPanel({ transitions, editStatus, onStatusChange }) {
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm">
      <CardHeader className="pb-2"><CardTitle className="font-heading text-sm font-bold tracking-tight">Update Status</CardTitle></CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-1.5">
          {transitions.map((s) => {
            const isSelected = editStatus === s;
            return (
              <Button key={s} variant={isSelected ? "default" : "outline"} size="sm" onClick={() => onStatusChange(s)}
                className={`rounded-sm text-xs h-7 capitalize ${isSelected ? "bg-[#003DA5] text-white hover:bg-[#002D7A]" : ""}`} data-testid={`status-transition-${s}`}>{STATUS_LABELS[s]}</Button>
            );
          })}
        </div>
        {editStatus && <p className="text-xs text-violet-600 mt-2" data-testid="status-change-hint">Add a comment below to confirm the status change to <span className="font-medium capitalize">{STATUS_LABELS[editStatus]}</span></p>}
      </CardContent>
    </Card>
  );
}

export function WatchersPanel({ watchers, isWatching, onToggleWatch }) {
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm" data-testid="watchers-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-sm font-bold tracking-tight">Watchers ({watchers.length})</CardTitle>
          <Button variant="ghost" size="sm" onClick={onToggleWatch} className="rounded-sm text-xs h-7 gap-1" data-testid="toggle-watch-btn">
            {isWatching ? <><EyeOff size={12} /> Unwatch</> : <><Eye size={12} /> Watch</>}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {watchers.length === 0 ? (
          <p className="text-xs text-[#52525B]" data-testid="no-watchers">No watchers</p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {watchers.map((w) => (
              <span key={w.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-zinc-100 rounded-sm text-[#09090B]" data-testid={`watcher-${w.user_id}`}>
                <User size={10} /> {w.user_name}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, children }) {
  return (<div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">{label}</span>{children}</div>);
}
